import { getDeeplinkPinTarget } from '@/features/deeplinks';

import type { LibrarySection } from '@/types';

interface FindPinnedIndexParams {
  sections: LibrarySection[];
  categorySlug: string | null;
  presetSlug: string | null;
}

const findPinnedIndex = ({
  sections,
  categorySlug,
  presetSlug,
}: FindPinnedIndexParams): number => {
  if (categorySlug) {
    const categoryIndex = sections.findIndex(
      (section) => section.slug === categorySlug
    );
    if (categoryIndex >= 0) return categoryIndex;
  }
  if (presetSlug) {
    return sections.findIndex((section) =>
      section.items.some((item) => item.slug === presetSlug)
    );
  }
  return -1;
};

/**
 * Moves the deeplinked section to the front of the library, keeping the
 * relative order of the rest, so `genaicategory` and `presetId` links land on
 * their category at first paint instead of scrolling to it. Used by both
 * surfaces — the mobile home rows and the desktop gallery.
 *
 * Pure: the target comes from the immutable deeplink snapshot and the index
 * from the sections passed in, so there is no effect, no store field, and no
 * intermediate render in the unpinned order. A slug that matches no section (or
 * is already first) returns the input array untouched.
 *
 * **Callers must memoize on `sections`** — the desktop gallery's tabs and
 * virtual rows memo on the returned array, and `useVisibleSection` has it in
 * effect deps, so a fresh identity per render would rebuild all of them.
 */
const pinDeeplinkSections = (sections: LibrarySection[]): LibrarySection[] => {
  const index = findPinnedIndex({ sections, ...getDeeplinkPinTarget() });
  if (index <= 0) return sections;

  return [
    sections[index],
    ...sections.slice(0, index),
    ...sections.slice(index + 1),
  ];
};

export default pinDeeplinkSections;
